import React, { useState } from 'react';
import { store } from '../../redux/oldStore';
import { toast } from 'react-toastify';
import FileDownload from 'js-file-download';
import { csvLine, splitCSV } from '../../utils/Tools';
import { ColumnData } from '../../utils/CommonTypes/CommonTypes';
import { ServerResponse } from '../../utils/Server';
import Modal from '../Modal/Modal';
import Icon, { IconType } from '../Icon/Icon';
import { UploadType } from './ParticipantsUpload';

interface IColumnMap {
  index: number;
  data: ColumnData;
  key: number;
}

interface IAwardColumn extends ColumnData {
  aliases?: string[];
}

interface ILoadSpecialAwardsResult {
  allImported: boolean;
  failedToImport: string[];
  totalImported: number;
}

const BASE_COLUMNS: IAwardColumn[] = [{ index: 0, id: 'index:0', value: 'Award Name', aliases: ['name', 'award', 'special award name', 'special award'] }];

const OPTIONAL_COLUMNS: IAwardColumn[] = [
  { id: 'award_description', value: 'Description' },
  { id: 'AwardSponsor', value: 'Award Sponsor', aliases: ['sponsor'] },
  { id: 'AwardPresentedBy', value: 'Award Presented By', aliases: ['presented by'] },
  { id: 'Prize', value: 'Prize' },
  { id: 'MonetaryValueInCents', value: 'Prize Monetary Value', aliases: ['monetary value', 'prize value'] },
  { id: 'points', value: 'Points' },
  { id: 'SortOrder', value: 'Sort Order' },
  { id: 'SubType', value: 'Sub Type' },
  { id: 'SubAwards', value: 'Sub Awards' },
  { id: 'GradeRange', value: 'Grade Range' },
  { id: 'GenderFilter', value: 'Gender' },
  { id: 'OnlyTeamEntries', value: 'Only Team Entries' },
  { id: 'OnlyNonTeamEntries', value: 'Only Non-Team Entries' },
  { id: 'IncludeInNomination', value: 'Include In Nomination' },
  { id: 'Active', value: 'Active' },
  { id: 'MaxNumberOfRecipients', value: 'Max Number of Recipients' },
  { id: 'PaidBy', value: 'Paid By' },
  { id: 'JudgedBy', value: 'Judged By' },
  { id: 'ProfessionalSocietyDonnerName', value: 'Professional Contact Name' },
  { id: 'ProfessionalSocietyDonnerEmail', value: 'Professional Contact Email' },
  { id: 'Donor', value: 'Donor' },
  { id: 'SpecialAwardsDivisions', value: 'Fair Division', aliases: ['division', 'divisions', 'fair divisions'] },
  { id: 'Categories', value: 'Category', aliases: ['categories'] },
  { id: 'AssociatedReport', value: 'Certificate Template' },
];

const ALL_COLUMNS: IAwardColumn[] = [...BASE_COLUMNS, ...OPTIONAL_COLUMNS];

const normalizeHeader = (value: string) => (value || '').toLowerCase().replace(/[\s\*\/_-]/g, '');

const findColumn = (header: string): IAwardColumn | undefined => {
  const normalized = normalizeHeader(header);
  if (!normalized) return undefined;
  return ALL_COLUMNS.find((col) => {
    if (normalizeHeader(col.value) === normalized || normalizeHeader(col.id) === normalized) return true;
    return col.aliases?.some((alias) => normalizeHeader(alias) === normalized);
  });
};

const SpecialAwardsUpload = () => {
  const [fileText, setFileText] = useState<string[][]>([]);
  const [headersIncluded, setHeadersIncluded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [parsingErrors, setParsingErrors] = useState<string[] | null>([]);
  const [additionalColumns, setAdditionalColumns] = useState<ColumnData[]>([]);
  const [uploadType, setUploadType] = useState<UploadType>('comma');
  const [showMapColumns, setShowMapColumns] = useState(false);
  const [columnMapping, setColumnMapping] = useState<Record<number, IColumnMap | undefined>>({});

  const applyMappedRows = (rows: string[][], mapping: Record<number, IColumnMap | undefined>, hasHeaders: boolean) => {
    const maps = Object.keys(mapping)
      .map((key) => mapping[+key])
      .filter((m): m is IColumnMap => !!m?.data);

    const additional: ColumnData[] = [];
    const additionalIndexes: number[] = [];
    maps.forEach((map) => {
      if (map.data.index === undefined) {
        additional.push(map.data);
        additionalIndexes.push(map.key);
      }
    });

    const baseIndexes = BASE_COLUMNS.map((col) => maps.find((m) => m.data.id === col.id)?.key ?? -1);
    const indexList = [...baseIndexes, ...additionalIndexes];
    const data = rows.map((row) => indexList.map((i) => row[i] ?? ''));

    setAdditionalColumns(additional);
    setFileText(data);
    setHeadersIncluded(hasHeaders);
    setShowMapColumns(false);
    setParsingErrors(null);
  };

  const processMappedFile = () => {
    const maps = Object.keys(columnMapping)
      .map((key) => columnMapping[+key])
      .filter((m): m is IColumnMap => !!m?.data);
    if (!maps.some((m) => m.data.id === BASE_COLUMNS[0].id)) {
      toast.error('Map a column to Award Name before processing the file.', { autoClose: false });
      return;
    }
    applyMappedRows(fileText, columnMapping, headersIncluded);
  };

  const tryAutoMap = (rows: string[][]): boolean => {
    if (!rows.length) return false;
    const headerValues = rows[0];
    const mapping: Record<number, IColumnMap | undefined> = {};
    const usedIds = new Set<string>();
    let allMatched = true;

    headerValues.forEach((header, i) => {
      const col = findColumn(header);
      if (!col || usedIds.has(col.id)) {
        allMatched = false;
        return;
      }
      usedIds.add(col.id);
      mapping[i] = { index: ALL_COLUMNS.indexOf(col), data: col, key: i };
    });

    if (allMatched && usedIds.has(BASE_COLUMNS[0].id)) {
      applyMappedRows(rows, mapping, true);
      return true;
    }
    return false;
  };

  const processTextFile = (text: string) => {
    const lines: string[][] = [];
    const perrors: string[] = [];
    const formattedText = text.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
    const rawLines = formattedText.split('\n');

    for (let i = 0; i < rawLines.length; i++) {
      const l = rawLines[i];
      if (l.length === 0) continue;

      const lineIndexes = csvLine(rawLines, i);
      let line = rawLines[lineIndexes[0]];
      for (let li = 1; li < lineIndexes.length; li++) {
        i = lineIndexes[li];
        line += '<br/>' + rawLines[lineIndexes[li]];
      }

      const lineValues = splitCSV(line, uploadType).map((v) => v.replace(/(\<br\/\>)/g, '\r\n'));
      lines.push(lineValues);
    }

    setLoaded(false);
    setColumnMapping({});
    setAdditionalColumns([]);
    setHeadersIncluded(false);

    if (!lines.length) {
      setFileText([]);
      setParsingErrors(['The file did not contain any rows.']);
      return;
    }

    if (tryAutoMap(lines)) return;

    const firstLooksLikeHeader = !!findColumn(lines[0][0]) || lines[0].some((cell) => !!findColumn(cell));
    if (firstLooksLikeHeader) setHeadersIncluded(true);

    if (lines.every((row) => row.length === 1)) {
      setFileText(lines);
      setParsingErrors(null);
      setShowMapColumns(false);
      return;
    }

    lines.forEach((row, index) => {
      if (row.length !== 1) {
        perrors.push(`Line ${index + 1} has ${row.length} columns, but should have 1 (Award Name). Map the extra columns.`);
      }
    });

    setFileText(lines);
    setParsingErrors(perrors);
    setShowMapColumns(true);
  };

  const awardFileSelected = (e: any) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (ev: any) => processTextFile(ev.target.result);
    reader.readAsText(file, 'utf-8-sig');
  };

  const load = () => {
    const realText = headersIncluded ? fileText.slice(1) : fileText;
    store.server
      .postApi<ServerResponse<ILoadSpecialAwardsResult>>('../Import/LoadSpecialAwards', {
        Data: realText,
        additionalColumns: additionalColumns.map((x) => x.id),
      })
      .then((x) => {
        if (x.Success) {
          if (!x.Value.allImported) {
            toast.warning(
              <>
                We were not able to import every award.
                <ul>
                  {x.Value.failedToImport.map((f, i) => (
                    <li key={`fti-${i}`}>{f}</li>
                  ))}
                </ul>
                Click to close this window
              </>,
              { autoClose: false },
            );
            setParsingErrors(x.Value.failedToImport);
            setLoaded(true);
          } else {
            toast.success('Your special awards have been loaded.');
            setLoaded(true);
          }
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });
  };

  const downloadTemplateClick = () => {
    FileDownload('Award Name\r\n', 'special_award_upload_template.csv');
  };

  const previewHeaders = [BASE_COLUMNS[0].value, ...additionalColumns.map((x) => x.value)];

  return (
    <>
      <div className="loading-container">
        <h1>Loading Special Award Information</h1>
        <div>
          You can load special awards using a CSV file. You can save Excel and Google Doc files as CSV files
          <ul>
            <li>
              <strong>Google Sheets</strong> Select File, then Download then as Comma-separated values (.csv)
            </li>
            <li>
              <strong>Excel</strong> Select File, Save as, then change type to CSV UTF-8 (*.csv)
            </li>
          </ul>
        </div>
        <div>
          <h3>Notes:</h3>
          <ul>
            <li>Award Name is the only required column</li>
            <li>Other award fields are optional. If your file has extra columns you will map them after upload</li>
            <li>Use semicolons to separate multiple Sub Awards, Fair Divisions, or Categories</li>
            <li>Grade Range can be entered as 6-8</li>
          </ul>
        </div>
        <div className="">
          <h3>Load Special Awards</h3>
          <div className="flex-between">
            <h4>File Format</h4>
            <button className="btn btn-secondary btn-xs" onClick={downloadTemplateClick}>
              Download Template
            </button>
          </div>
          <div>
            The Special Award CSV file should include Award Name. Other columns are optional and can be mapped after upload.
            <ul>
              <li>
                <strong>Award Name</strong> (required)
              </li>
              {OPTIONAL_COLUMNS.map((col) => (
                <li key={col.id}>{col.value}</li>
              ))}
            </ul>
          </div>
          {!loaded && (
            <div className="">
              <div className="flex bumper-l"></div>
              <div className="bumper-l">
                <label>Delimiter: </label>
                <select className="form-control" onChange={(e) => setUploadType(e.target.value as UploadType)} value={uploadType}>
                  <option value={'comma'}>Comma</option>
                  <option value={'tab'}>Tab</option>
                </select>
              </div>
              <div className="bumper-l">
                <label className="">Special Award File:</label>
                <input type="file" accept=".csv" onChange={awardFileSelected} onClick={(evt: any) => (evt.target.value = null)} />
              </div>

              {fileText && fileText.length > 0 && (
                <div className="csv-file-preview">
                  <table>
                    {!headersIncluded && (
                      <thead>
                        <tr>
                          {previewHeaders.map((header, i) => (
                            <th key={i}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {fileText.map((r, ri) => (
                        <tr key={'r' + ri}>
                          {r.map((v, vi) => {
                            if (headersIncluded && ri === 0) {
                              return <th key={ri + '-' + vi}>{v}</th>;
                            }
                            return <td key={ri + '-' + vi}>{v}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {parsingErrors && parsingErrors.length > 0 && (
                <table>
                  {parsingErrors.map((error, index) => (
                    <tr key={index}>
                      <td>{error}</td>
                    </tr>
                  ))}
                </table>
              )}
            </div>
          )}

          {!loaded && fileText && fileText.length > 0 && (
            <div className="ready-to-load-area">
              <h4>If the Special Award file contents look good, press Load</h4>
              <button type="button" className="btn btn-success" onClick={load}>
                <i className="fad fa-cloud-upload"></i>
                Load
              </button>
            </div>
          )}
          {loaded && (
            <div>
              <h1>Special Awards loaded!</h1>
              If you need to load more special awards refresh your page.
            </div>
          )}
        </div>
      </div>

      {showMapColumns && (
        <Modal setModalOpen={setShowMapColumns} title="Map Columns to Load Data" size="l" noClose={true}>
          <div className="form-horizontal">
            <h4>
              <Icon type={IconType.mapO} />
              Map Columns to Data Points
            </h4>
            Map each column from your file to a data point. Award Name is required; all other fields are optional.
            <div className="form-group">
              <div className="col-sm-offset-4 col-sm-8">
                <input type="checkbox" id="headers-awards" className="" onChange={(e) => setHeadersIncluded(e.target.checked)} defaultChecked={headersIncluded} />
                <label htmlFor="headers-awards" className="control-label">
                  First row are data headers
                </label>
              </div>
            </div>
            {fileText &&
              fileText.length > (headersIncluded ? 1 : 0) &&
              fileText[0].map((x, i) => (
                <div className="form-group" key={i}>
                  <label className="col-sm-4 control-label" htmlFor={`map-column-${i}-award`}>
                    {headersIncluded ? (
                      <>
                        Column {i + 1}: <br />
                        {x}
                      </>
                    ) : (
                      <>
                        Column {i + 1}: <br />({x.substring(0, 25)})
                      </>
                    )}
                  </label>
                  <div className="col-sm-8">
                    <select
                      id={`map-column-${i}-award`}
                      className="form-control"
                      onChange={(e) => {
                        const index = +e.target.value;
                        setColumnMapping((prev) => {
                          const mapping = { ...prev };
                          mapping[i] = index > -1 ? { index, data: ALL_COLUMNS[index], key: i } : undefined;
                          return mapping;
                        });
                      }}>
                      <option value="-1">Select data point...</option>
                      {ALL_COLUMNS.map((c, ci) => (
                        <option key={`award-${i}-${ci}`} value={ci}>
                          {c.value}
                          {c.index === 0 ? ' (required)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            <div className="form-group">
              <div className="col-sm-4"></div>
              <div className="col-sm-8">
                <button type={'button'} className="btn btn-secondary" onClick={processMappedFile}>
                  <Icon type={IconType.upload} /> Process File
                </button>
                <button
                  type={'button'}
                  className="btn btn-default"
                  onClick={() => {
                    setShowMapColumns(false);
                  }}>
                  <Icon type={IconType.close} /> Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SpecialAwardsUpload;
